import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { 
  Settings as SettingsIcon,
  Database,
  Mail,
  Shield,
  Bell,
  Globe,
  Key,
  Server,
  Users,
  AlertTriangle,
  CheckCircle,
  Save,
  RotateCcw
} from 'lucide-react'

const Settings = () => {
  const [settings, setSettings] = useState({
    // System Configuration
    systemName: 'Adarsh degree college Fee Management',
    systemVersion: 'v1.0.0',
    maintenanceMode: false,
    
    // Database Settings
    dbHost: 'localhost',
    dbPort: 5432,
    dbName: 'college_fee_system',
    dbBackupInterval: '24',
    
    // Email Configuration
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    emailFrom: 'noreply@adarshauniversity.edu.in',
    
    // Security Settings
    jwtSecret: '***********',
    sessionTimeout: '24',
    maxLoginAttempts: '5',
    
    // Notification Settings
    enableEmailNotifications: true,
    enableSMSNotifications: false,
    reminderDaysBefore: '7',
    
    // Payment Settings
    paymentGatewayMode: 'test',
    currency: 'INR',
    lateFeePercentage: '5',
    
    // System Limits
    maxUsersPerCollege: '500',
    maxColleges: '50',
    fileUploadLimit: '10'
  })
  
  const [isSaving, setIsSaving] = useState(false)
  const [savedRecently, setSavedRecently] = useState(false)

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    setIsSaving(false)
    setSavedRecently(true)
    setTimeout(() => setSavedRecently(false), 3000)
  }

  const handleReset = () => {
    // Reset to default values
    setSettings({
      systemName: 'Adarsh degree college Fee Management',
      systemVersion: 'v1.0.0',
      maintenanceMode: false,
      dbHost: 'localhost',
      dbPort: 5432,
      dbName: 'college_fee_system',
      dbBackupInterval: '24',
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      emailFrom: 'noreply@adarshauniversity.edu.in',
      jwtSecret: '***********',
      sessionTimeout: '24',
      maxLoginAttempts: '5',
      enableEmailNotifications: true,
      enableSMSNotifications: false,
      reminderDaysBefore: '7',
      paymentGatewayMode: 'test',
      currency: 'INR',
      lateFeePercentage: '5',
      maxUsersPerCollege: '500',
      maxColleges: '50',
      fileUploadLimit: '10'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600">Configure and manage system-wide settings</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {savedRecently && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center space-x-2 text-green-600"
            >
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Settings saved!</span>
            </motion.div>
          )}
          
          <Button 
            variant="outline" 
            onClick={handleReset}
            className="flex items-center space-x-2"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset</span>
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-blue-600" />
                <span>System Configuration</span>
              </CardTitle>
              <CardDescription>Basic system settings and information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Name
                </label>
                <Input
                  value={settings.systemName}
                  onChange={(e) => handleInputChange('systemName', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version
                </label>
                <Input
                  value={settings.systemVersion}
                  onChange={(e) => handleInputChange('systemVersion', e.target.value)}
                  disabled
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maintenance Mode
                  </label>
                  <p className="text-sm text-gray-500">Enable system maintenance mode</p>
                </div>
                <Button
                  variant={settings.maintenanceMode ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('maintenanceMode', !settings.maintenanceMode)}
                >
                  {settings.maintenanceMode ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Database Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="h-5 w-5 text-green-600" />
                <span>Database Configuration</span>
              </CardTitle>
              <CardDescription>Database connection and backup settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Host
                  </label>
                  <Input
                    value={settings.dbHost}
                    onChange={(e) => handleInputChange('dbHost', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Port
                  </label>
                  <Input
                    type="number"
                    value={settings.dbPort}
                    onChange={(e) => handleInputChange('dbPort', parseInt(e.target.value))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Database Name
                </label>
                <Input
                  value={settings.dbName}
                  onChange={(e) => handleInputChange('dbName', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backup Interval (hours)
                </label>
                <Input
                  type="number"
                  value={settings.dbBackupInterval}
                  onChange={(e) => handleInputChange('dbBackupInterval', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-purple-600" />
                <span>Email Configuration</span>
              </CardTitle>
              <CardDescription>SMTP settings for email notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP Host
                  </label>
                  <Input
                    value={settings.smtpHost}
                    onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMTP Port
                  </label>
                  <Input
                    type="number"
                    value={settings.smtpPort}
                    onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Email
                </label>
                <Input
                  type="email"
                  value={settings.emailFrom}
                  onChange={(e) => handleInputChange('emailFrom', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Security Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-red-600" />
                <span>Security Settings</span>
              </CardTitle>
              <CardDescription>Authentication and security configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JWT Secret Key
                </label>
                <Input
                  type="password"
                  value={settings.jwtSecret}
                  onChange={(e) => handleInputChange('jwtSecret', e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (hours)
                  </label>
                  <Input
                    type="number"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleInputChange('sessionTimeout', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Login Attempts
                  </label>
                  <Input
                    type="number"
                    value={settings.maxLoginAttempts}
                    onChange={(e) => handleInputChange('maxLoginAttempts', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Notification Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-orange-600" />
                <span>Notification Settings</span>
              </CardTitle>
              <CardDescription>Configure system notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email Notifications
                  </label>
                  <p className="text-sm text-gray-500">Send email notifications to users</p>
                </div>
                <Button
                  variant={settings.enableEmailNotifications ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('enableEmailNotifications', !settings.enableEmailNotifications)}
                >
                  {settings.enableEmailNotifications ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SMS Notifications
                  </label>
                  <p className="text-sm text-gray-500">Send SMS notifications to users</p>
                </div>
                <Button
                  variant={settings.enableSMSNotifications ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('enableSMSNotifications', !settings.enableSMSNotifications)}
                >
                  {settings.enableSMSNotifications ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder Days Before Due Date
                </label>
                <Input
                  type="number"
                  value={settings.reminderDaysBefore}
                  onChange={(e) => handleInputChange('reminderDaysBefore', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Payment Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Key className="h-5 w-5 text-indigo-600" />
                <span>Payment Settings</span>
              </CardTitle>
              <CardDescription>Payment gateway and fee configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Gateway Mode
                </label>
                <div className="flex space-x-2">
                  <Button
                    variant={settings.paymentGatewayMode === 'test' ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleInputChange('paymentGatewayMode', 'test')}
                  >
                    Test Mode
                  </Button>
                  <Button
                    variant={settings.paymentGatewayMode === 'live' ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleInputChange('paymentGatewayMode', 'live')}
                  >
                    Live Mode
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <Input
                    value={settings.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Fee Percentage
                  </label>
                  <Input
                    type="number"
                    value={settings.lateFeePercentage}
                    onChange={(e) => handleInputChange('lateFeePercentage', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* System Limits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="h-5 w-5 text-gray-600" />
              <span>System Limits</span>
            </CardTitle>
            <CardDescription>Configure system capacity and limits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Users Per College
                </label>
                <Input
                  type="number"
                  value={settings.maxUsersPerCollege}
                  onChange={(e) => handleInputChange('maxUsersPerCollege', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Colleges
                </label>
                <Input
                  type="number"
                  value={settings.maxColleges}
                  onChange={(e) => handleInputChange('maxColleges', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Upload Limit (MB)
                </label>
                <Input
                  type="number"
                  value={settings.fileUploadLimit}
                  onChange={(e) => handleInputChange('fileUploadLimit', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* System Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <span>System Status</span>
            </CardTitle>
            <CardDescription>Current system health and status indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Database</p>
                  <p className="text-xs text-green-600">Connected</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Email Service</p>
                  <p className="text-xs text-green-600">Active</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Payment Gateway</p>
                  <p className="text-xs text-yellow-600">Test Mode</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">API Server</p>
                  <p className="text-xs text-green-600">Running</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

export default Settings
